import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import CircularProgress from '@material-ui/core/CircularProgress'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import Chip from '@material-ui/core/Chip'

export interface BulkOperationProgress {
    offeringId: string | number
    offeringName: string
    operation: 'create' | 'update' | 'delete'
    status: 'pending' | 'success' | 'failed'
    error?: string
}

interface BulkProgressListProps {
    progress: BulkOperationProgress[]
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
        maxHeight: '500px',
        overflowY: 'auto',
    },
    operationChip: {
        marginRight: theme.spacing(2),
        fontSize: '0.75rem',
        height: '20px',
    },
    successIcon: {
        color: theme.palette.success.main,
    },
    errorIcon: {
        color: theme.palette.error.main,
    },
    listItem: {
        paddingLeft: 0,
        paddingRight: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        marginTop: theme.spacing(1),
    },
}))

export const BulkProgressList: React.FC<BulkProgressListProps> = ({ progress }) => {
    const classes = useStyles()

    const getOperationLabel = (op: 'create' | 'update' | 'delete', status: 'pending' | 'success' | 'failed') => {
        if (status === 'pending') {
            switch (op) {
                case 'create':
                    return { label: 'Assigning', color: 'primary' as const }
                case 'delete':
                    return { label: 'Removing', color: 'secondary' as const }
                case 'update':
                    return { label: 'Updating', color: 'default' as const }
            }
        } else if (status === 'success') {
            switch (op) {
                case 'create':
                    return { label: 'Assigned', color: 'primary' as const }
                case 'delete':
                    return { label: 'Removed', color: 'secondary' as const }
                case 'update':
                    return { label: 'Updated', color: 'default' as const }
            }
        } else {
            return { label: 'Failed', color: 'secondary' as const }
        }
    }

    const getStatusIcon = (status: 'pending' | 'success' | 'failed') => {
        switch (status) {
            case 'pending':
                return <CircularProgress size={20} />
            case 'success':
                return <CheckCircleIcon className={classes.successIcon} />
            case 'failed':
                return <CancelIcon className={classes.errorIcon} />
        }
    }

    return (
        <Paper className={classes.root} variant="outlined">
            <Box mb={3}>
                <Typography variant="h6">Bulk Operation Status</Typography>
            </Box>
            <List disablePadding>
                {progress.map((item, index) => {
                    const opInfo = getOperationLabel(item.operation, item.status)
                    return (
                        <ListItem key={`${item.offeringId}-${item.operation}-${index}`} className={classes.listItem} alignItems="flex-start">
                            <ListItemIcon style={{ minWidth: '40px', marginTop: '4px' }}>
                                {getStatusIcon(item.status)}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box display="flex" alignItems="center" flexWrap="wrap">
                                        <Chip
                                            label={opInfo.label}
                                            color={opInfo.color}
                                            className={classes.operationChip}
                                            variant="outlined"
                                        />
                                        <Typography variant="body1">{item.offeringName}</Typography>
                                    </Box>
                                }
                                secondary={
                                    item.status === 'failed' && item.error ? (
                                        <Typography className={classes.errorText} component="span">
                                            {item.error}
                                        </Typography>
                                    ) : null
                                }
                            />
                        </ListItem>
                    )
                })}
            </List>
        </Paper>
    )
}

export default BulkProgressList
